import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountReport } from '../function/type'; // Thay đổi đường dẫn import types phù hợp với dự án
import { Tooltip } from 'antd';
import { CaretUpIcon, InfoIcon } from '@phosphor-icons/react';

interface StatisticsOverviewProps {
  report?: AccountReport;
}

// Helper format tiền tệ USD
const formatCurrency = (val?: number): string => {
  if (val === undefined || val === null) return '$0.00';
  const isNegative = val < 0;
  const formatted = Math.abs(val).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return isNegative ? `-$${formatted}` : `$${formatted}`;
};

// Helper format số thập phân
const formatNumber = (val?: number, decimals: number = 2): string => {
  if (val === undefined || val === null) return '0.00';
  return val.toFixed(decimals);
};

// Helper format tỷ lệ phần trăm (xử lý cả dạng 0.396 hoặc 39.6)
const formatPercent = (val?: number): string => {
  if (val === undefined || val === null) return '0.0%';
  const pct = val <= 1 && val > 0 ? val * 100 : val;
  return `${pct.toFixed(1)}%`;
};

const StatisticsOverview: React.FC<StatisticsOverviewProps> = ({ report }) => {
  const { t } = useTranslation();
  const data = report?.data;
  const [openDetails, setOpenDetails] = React.useState(true);

  // 1. Tính toán biến cho phần Thống kê Tổng quan (UI Mới chuẩn HTML)
  const totalTrades = data?.total_trades ?? 0;
  const sumVolume = data?.sum_volume ?? 0;
  const winRate = data?.win_rate ?? 0;
  const winRatePercent = winRate <= 1 && winRate > 0 ? winRate * 100 : winRate;
  const profitFactor = data?.profit_factor ?? 0;

  const netProfit = data?.raw_trades?.reduce((acc: number, t: any) => {
    const gross = t.profit_gross ?? ((t.net_profit || 0) - (t.commission || 0) - (t.swap || 0));
    return acc + gross;
  }, 0) ?? (data?.sum_net_profit ?? 0);

  const bestTrade = data?.performance_summary?.best_trade?.profit ?? data?.risk_management?.risk_metrics?.largest_win ?? 0;
  const avgWin = data?.mean_profitable ?? data?.performance_summary?.avg_profit_per_win ?? 0;

  const worstTradeLoss = typeof (data?.performance_summary?.worst_trade as any) === 'number'
    ? (data?.performance_summary?.worst_trade as any)
    : data?.performance_summary?.worst_trade?.loss;
  const largestLoss = data?.risk_management?.risk_metrics?.largest_loss;

  const rawWorstTrade = (worstTradeLoss !== undefined && worstTradeLoss <= 0)
    ? worstTradeLoss
    : (largestLoss !== undefined && largestLoss <= 0)
      ? largestLoss
      : 0;

  const worstTradeVal = rawWorstTrade < 0 ? -Math.abs(rawWorstTrade) : 0;

  const winningTradesCount = data?.performance_summary?.profitable_trades ?? Math.round((totalTrades * winRatePercent) / 100);
  const losingTradesCount = data?.performance_summary?.losing_trades ?? (totalTrades - winningTradesCount);

  // 2. GIỮ NGUYÊN DỮ LIỆU BÊN DƯỚI TỪ FILE CŨ
  // Chỉ số nâng cao
  const advancedMetrics = [
    { label: t('metricStats.labels.riskRewardRatio'), value: `1 : ${formatNumber(data?.mean_RRR, 2)}` },
    { label: t('metricStats.labels.sharpeRatio'), value: formatNumber(data?.sharpe_ratio, 2), color: '!text-emerald-500' },
    { label: t('metricStats.labels.expectedPayoff'), value: formatCurrency(data?.expectancy) },
  ];

  // Tần suất / Ngày giao dịch
  const countDays = data?.count_trading_days ?? 0;
  const avgTradesPerDay = countDays > 0 && data?.total_trades ? (data.total_trades / countDays).toFixed(1) : '0';
  const timeMetrics = [
    { label: t('metricStats.labels.tradingDays'), value: `${countDays} ${t('metricStats.labels.daysSuffix')}` },
    { label: t('metricStats.labels.avgTradesPerDay'), value: `${avgTradesPerDay} ${t('metricStats.labels.tradesSuffix')}` },
    { label: t('metricStats.labels.avgProfitPerDay'), value: formatCurrency(countDays > 0 && data?.sum_net_profit ? data.sum_net_profit / countDays : 0) },
  ];

  // Chỉ số khối lượng
  const avgVol = data?.risk_management?.average_volume ?? (data?.total_trades && data?.sum_volume ? data.sum_volume / data.total_trades : 0);
  const volumeMetrics = [
    { label: t('metricStats.labels.totalVolumeLot'), value: formatNumber(data?.sum_volume, 2) },
    { label: t('metricStats.labels.maxVolume'), value: formatNumber(data?.risk_management?.max_volume, 2) },
    { label: t('metricStats.labels.avgVolumePerTrade'), value: formatNumber(avgVol, 2) },
  ];

  // Tính đa dạng & Kỷ lục
  const symbolList = data?.symbol_analysis ?? [];
  const profitableSymbols = symbolList.filter((s: any) => s.total_profit > 0).length;

  const diversityMetrics = [
    { label: t('metricStats.labels.tradedSymbols'), value: `${symbolList.length}` },
    { label: t('metricStats.labels.profitableSymbols'), value: `${profitableSymbols}`, color: '!text-emerald-500' },
    {
      label: t('metricStats.labels.maxConsecutiveWins'),
      value: `${data?.behavioral_analysis?.max_consecutive_wins ?? 0}`,
      color: '!text-emerald-500',
    },
    {
      label: t('metricStats.labels.maxConsecutiveLosses'),
      value: `${data?.behavioral_analysis?.max_consecutive_losses ?? 0}`,
      color: '!text-rose-500',
    },
    {
      label: t('metricStats.labels.maxDrawdown'),
      value: formatPercent(data?.risk_management?.max_drawdown_percent),
      color: '!text-rose-500',
    },
  ];

  return (
    <div className="card-premium p-6 lg:p-8 bg-white border border-slate-200/80 rounded-2xl shadow-sm">

      {/* ================= SECTION 1: PHẦN TỔNG QUAN THỐNG KÊ GIAO DỊCH (UI HTML MỚI) ================= */}
      <h2 className="!text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
        {t('metricStats.title') || 'Tổng quan Thống kê Giao dịch'}
        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded !text-[10px] uppercase font-bold tracking-wider ml-auto">
          {t('metricStats.currentPeriod') || 'Kỳ hiện tại'}
        </span>
      </h2>

      {/* Grid 4 Cột / 4 Nhóm chuẩn HTML */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6 pb-2">

        {/* Nhóm 1: Tần suất & Khối lượng */}
        <div className="space-y-6">
          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-regular fa-file-lines text-slate-300"></i>
              {t('metricStats.metrics.totalTrades') || 'Tổng số giao dịch'}
              <Tooltip title={t('metricStats.metrics.totalTradesTooltipDesc') || 'Tổng số giao dịch được thực hiện trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-800 font-mono-num tracking-tight">
                {totalTrades}
              </span>
              <span className="text-xs font-medium text-slate-400">
                {t('metricStats.labels.tradesSuffix') || 'lệnh'}
              </span>
            </div>
          </div>

          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-solid fa-arrow-trend-up text-blue-300"></i>
              {t('metricStats.metrics.volumeLot') || 'Khối lượng (Lot)'}
              <Tooltip title={t('metricStats.metrics.volumeLotTooltipDesc') || 'Tổng khối lượng giao dịch trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-blue-600 font-mono-num tracking-tight">
                {formatNumber(sumVolume, 2)}
              </span>
              <span className="text-xs font-medium text-blue-300">lots</span>
            </div>
          </div>
        </div>

        {/* Nhóm 2: Hiệu suất & Rủi ro */}
        <div className="space-y-6 lg:pl-6 lg:border-l border-slate-100/80">
          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-solid fa-chart-pie text-indigo-300"></i>
              {t('metricStats.metrics.winRate') || 'Tỉ lệ thắng'}
              <Tooltip title={t('metricStats.metrics.winRateTooltipDesc') || 'Tỉ lệ thắng trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-indigo-600 font-mono-num tracking-tight">
                {formatPercent(winRate)}
              </span>
              <div className="w-16 h-1.5 bg-rose-100 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-indigo-500 rounded-r-full"
                  style={{ width: `${Math.min(100, Math.max(0, winRatePercent))}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-solid fa-scale-balanced text-slate-300"></i>
              {t('metricStats.metrics.profitFactor') || 'Hệ số lợi nhuận'}
              <Tooltip title={t('metricStats.metrics.profitFactorTooltipDesc') || 'Hệ số lợi nhuận trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-slate-700 font-mono-num tracking-tight">
                {formatNumber(profitFactor, 2)}
              </span>
              {profitFactor < 1 && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-500">
                  {t('metricStats.warning') || 'CẢNH BÁO'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Nhóm 3: PnL Tổng quát & Lãi cao nhất */}
        <div className="space-y-6 lg:pl-6 lg:border-l border-slate-100/80">
          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-solid fa-wallet text-slate-300"></i>
              {t('metricStats.metrics.netProfit') || 'Lợi nhuận ròng'}
              <Tooltip title={t('metricStats.metrics.netProfitTooltipDesc') || 'Lợi nhuận ròngF trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-slate-800 font-mono-num tracking-tight">
                {formatCurrency(netProfit)}
              </span>
              {netProfit < 0 ? (
                <i className="fa-solid fa-arrow-down text-rose-500 text-xs"></i>
              ) : (
                <i className="fa-solid fa-arrow-up text-emerald-500 text-xs"></i>
              )}
            </div>
          </div>

          <div className="relative group flex items-end justify-between pr-2 sm:pr-4">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <i className="fa-solid fa-trophy text-emerald-300"></i>
                {t('metricStats.metrics.bestTrade') || 'Lãi cao nhất'}
                <Tooltip title={t('metricStats.metrics.bestTradeTooltipDesc') || 'Lãi cao nhất trong kỳ báo cáo.'}>
                  <InfoIcon size={16} />
                </Tooltip>
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-emerald-500 font-mono-num tracking-tight">
                  {formatCurrency(bestTrade)}
                </span>
              </div>
            </div>
            <div className="text-right border-l border-slate-100 pl-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5 block">
                {t('metricStats.winningTrades') || 'Lệnh thắng'}
              </span>
              <span className="text-lg font-bold text-emerald-600 font-mono-num tracking-tight">
                {winningTradesCount}
              </span>
            </div>
          </div>
        </div>

        {/* Nhóm 4: Phân tích Chi tiết (Trung bình / Thua cao nhất) */}
        <div className="space-y-6 lg:pl-6 lg:border-l border-slate-100/80">
          <div className="relative group">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <i className="fa-solid fa-arrow-right text-emerald-300"></i>
              {t('metricStats.metrics.averageWin') || 'Trung bình thắng'}
              <Tooltip title={t('metricStats.metrics.averageWinTooltipDesc') || 'Lợi nhuận trung bình của các lệnh thắng trong kỳ báo cáo.'}>
                <InfoIcon size={16} />
              </Tooltip>
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-emerald-500 font-mono-num tracking-tight">
                {formatCurrency(avgWin)}
              </span>
            </div>
          </div>

          <div className="relative group flex items-end justify-between pr-2 sm:pr-4">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <i className="fa-solid fa-arrow-trend-down text-rose-300"></i>
                {t('metricStats.metrics.worstTrade') || 'Lỗ cao nhất'}
                <Tooltip title={t('metricStats.metrics.worstTradeTooltipDesc') || 'Lỗ cao nhất trong kỳ báo cáo.'}>
                  <InfoIcon size={16} />
                </Tooltip>
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-rose-500 font-mono-num tracking-tight">
                  {worstTradeVal < 0 && formatCurrency(worstTradeVal) || <>0.00</>}
                </span>
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse hidden sm:block"></div>
              </div>
            </div>
            <div className="text-right border-l border-slate-100 pl-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5 block">
                {t('metricStats.losingTrades') || 'Lệnh thua'}
              </span>
              <span className="text-lg font-bold text-rose-600 font-mono-num tracking-tight">
                {losingTradesCount}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end col-span-full my-4 ">
        <button
          className="flex !flex-row justify-center items-center gap-2 !text-xs text-slate-400 hover:text-slate-600 focus:outline-none"
          onClick={() => setOpenDetails(!openDetails)}
        >
          {openDetails ? t('metricStats.hideDetails') || 'Ẩn chi tiết' : t('metricStats.showDetails') || 'Xem chi tiết'}
          <CaretUpIcon size={18} className={`transition-transform ${openDetails ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* ================= SECTION 2 ================= */}
      {
        openDetails && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-8 pt-8 border-t border-slate-100">

            {/* Cột 1: Chỉ số nâng cao & Thời gian/Tần suất */}
            <div>
              <div>
                <h4 className="!text-[11px] uppercase font-bold !text-slate-800 tracking-widest mb-3">
                  {t('metricStats.sections.advancedMetrics')}
                </h4>
                <div className="space-y-0 !text-sm">
                  {advancedMetrics.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2.5 border-b border-dotted border-slate-300">
                      <span className="!text-slate-500">{item.label}</span>
                      <span className={`font-bold font-mono-num ${item.color || '!text-slate-800'}`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h4 className="!text-[11px] uppercase font-bold !text-slate-800 tracking-widest mb-3">
                  {t('metricStats.sections.tradingFrequency')}
                </h4>
                <div className="space-y-0 !text-sm">
                  {timeMetrics.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2.5 border-b border-dotted border-slate-300">
                      <span className="!text-slate-500">{item.label}</span>
                      <span className="font-bold !text-slate-800 font-mono-num">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cột 2: Đa dạng & Kỷ lục */}
            <div>
              <h4 className="!text-[11px] uppercase font-bold !text-slate-800 tracking-widest mb-3">
                {t('metricStats.sections.diversityAndRecords')}
              </h4>
              <div className="space-y-0 !text-sm">
                {diversityMetrics.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2.5 border-b border-dotted border-slate-300">
                    <span className="!text-slate-500">{item.label}</span>
                    <span className={`font-bold font-mono-num ${item.color || '!text-slate-800'}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cột 3: Khối lượng */}
            <div>
              <div className="mb-8">
                <h4 className="!text-[11px] uppercase font-bold !text-slate-800 tracking-widest mb-3">
                  {t('metricStats.sections.volumeMetrics')}
                </h4>
                <div className="space-y-0 !text-sm">
                  {volumeMetrics.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2.5 border-b border-dotted border-slate-300">
                      <span className="!text-slate-500">{item.label}</span>
                      <span className="font-bold !text-slate-800 font-mono-num">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        )
      }

    </div>
  );
};

export default StatisticsOverview;